import { useCallback, useMemo, useState } from 'react';

import dlv from 'dlv';

import { GetMeDocument, useSetLoyaltyRewardsTierMutation } from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { useAuthContext } from 'state/auth';
import { NUM_RECENT_PURCHASED_ITEMS } from 'state/auth/hooks/use-current-user';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLogger } from 'state/logger';
import { ILoyaltyCategory, ILoyaltyTier } from 'state/loyalty/types';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import noop from 'utils/noop';

export type SelectedRewardsTierOptions = Partial<{
  onSetRewardsTierError: () => void;
  loyaltyTiers: ILoyaltyTier[];
}>;

type LoyaltyCategoryOption = ILoyaltyCategory['options'][number];

const formatLoyaltyCategories = (loyaltyCategories: ILoyaltyCategory[]) =>
  loyaltyCategories.reduce((acc: { [key: string]: LoyaltyCategoryOption }, { options }) => {
    options.forEach(val => (acc[val._id] = val));
    return acc;
  }, {});

export const useSelectedReward = (
  loyaltyCategories: ILoyaltyCategory[] | null = [],
  { loyaltyTiers, onSetRewardsTierError = noop }: SelectedRewardsTierOptions = {}
) => {
  const { user } = useAuthContext();
  const shouldUniqueByModifiers = useFlag(LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS);
  const { logEvent } = useMParticleContext();
  const logger = useLogger();
  const { activeCard } = useLoyaltyActiveCard();
  const [setSelectedRewardsTier] = useSetLoyaltyRewardsTierMutation();

  const { cardId = '' } = activeCard || {};

  const selectedRewardTierId = useMemo(() => dlv(user || {}, 'details.loyaltyTier') ?? null, [
    user,
  ]);

  const [updatingRewardTier, setUpdatingRewardTier] = useState(false);

  const logRewardTierChangedEvent = useCallback(
    (rewardTierId: string, success: boolean, message?: string) => {
      logEvent(CustomEventNames.REWARD_TIER_CHANGED, EventTypes.UserPreference, {
        'Reward Tier ID': rewardTierId,
        Response: success ? 'Successful' : 'Failure',
        'Response Description': success ? 'Successful' : message,
      });
    },
    [logEvent]
  );

  const onChangeRewardTier = useCallback(
    async (newLoyaltyTierId: string | null) => {
      if (!newLoyaltyTierId || !cardId) {
        return;
      }

      setUpdatingRewardTier(true);

      try {
        await setSelectedRewardsTier({
          variables: {
            cardId,
            rewardsTier: newLoyaltyTierId,
          },
          refetchQueries: [
            {
              query: GetMeDocument,
              variables: {
                customInput: { shouldUniqueByModifiers },
                numUniquePurchasedItems: NUM_RECENT_PURCHASED_ITEMS,
              },
            },
          ],
          awaitRefetchQueries: true,
        });
        logRewardTierChangedEvent(newLoyaltyTierId, true);
      } catch (error) {
        logger.error({ error });
        logRewardTierChangedEvent(newLoyaltyTierId, false, error);
        onSetRewardsTierError();
      } finally {
        setUpdatingRewardTier(false);
      }
    },
    [
      cardId,
      logRewardTierChangedEvent,
      logger,
      onSetRewardsTierError,
      setSelectedRewardsTier,
      shouldUniqueByModifiers,
    ]
  );

  const selectedRewardTier = useMemo(() => {
    if (loyaltyCategories) {
      const loyaltyCategoriesById = formatLoyaltyCategories(loyaltyCategories);
      return loyaltyCategoriesById[selectedRewardTierId ?? ''];
    }
    return undefined;
  }, [selectedRewardTierId, loyaltyCategories]);

  const selectedRewardCategoryId = useMemo(() => {
    // if we have loyaltyTiers this is an onboarding update.
    if (loyaltyCategories === null && loyaltyTiers && loyaltyTiers.length) {
      const selectedTier = loyaltyTiers.find(({ _id }) => _id === selectedRewardTierId);
      return selectedTier?._id;
    }
    if (loyaltyCategories && loyaltyCategories.length) {
      const selected = loyaltyCategories.find(({ options }) =>
        options.find(({ _id }) => _id === selectedRewardTierId)
      );
      return selected?._id;
    }
    return undefined;
  }, [loyaltyTiers, loyaltyCategories, selectedRewardTierId]);

  return {
    selectedRewardCategoryId,
    selectedRewardTier,
    selectedRewardTierId,
    onChangeRewardTier,
    updatingRewardTier,
  };
};
